import { toast } from "react-hot-toast";
import { useSupabaseCallback } from "../../../server/supabase/hooks";
import { CeAdminSupabaseAccountService } from "../../generic/server/supabase-account-service";
import { useTriggerPendoUpdate } from "../components/use-trigger-pendo-update";

import type { SupabaseCallback } from "../../../server/supabase/hooks/use-supabase-callback";
import type { AdminAccountLimit } from "../../generic/server/supabase-account-service";

export type AccountLimitsUpdate = Partial<
  Pick<
    AdminAccountLimit,
    | "business_name"
    | "usage"
    | "churned"
    | "enable_slack"
    | "enable_teams"
    | "enable_twilio"
    | "enable_whatsapp"
    | "enable_work_email"
    | "enable_personal_email"
    | "ses_configuration_set"
    | "limits"
  >
>;

/**
 * The upsert_account_limits RPC takes the whole row, so a partial section
 * update is merged over the loaded account before saving.
 *
 * On success `onSaved` receives the merged account so the page can patch its
 * state in place — saving one section must not reload the page and discard
 * unsaved edits in the others.
 */
export const useSaveAccountLimits = (
  account: AdminAccountLimit | null,
  onSaved: (account: AdminAccountLimit) => void,
): SupabaseCallback<[AccountLimitsUpdate], void> => {
  const { accountUpdated } = useTriggerPendoUpdate();

  return useSupabaseCallback(
    async ({ supabase }, update: AccountLimitsUpdate) => {
      if (!account) return;

      const merged = { ...account, ...update };

      const { error } = await new CeAdminSupabaseAccountService(supabase).setAccountLimits({
        account_id: account.id,
        limits: merged.limits ?? {},
        business_name: merged.business_name,
        usage: merged.usage,
        churned: merged.churned,
        enable_slack: merged.enable_slack,
        enable_teams: merged.enable_teams,
        enable_twilio: merged.enable_twilio,
        enable_whatsapp: merged.enable_whatsapp,
        enable_work_email: merged.enable_work_email,
        enable_personal_email: merged.enable_personal_email,
        ses_configuration_set: merged.ses_configuration_set,
      });

      if (error) {
        toast.error("Could not save account settings");
        return;
      }

      toast.success(`Saved account settings for ${account.name}`);

      await accountUpdated({
        id: account.id,
        name: account.name,
        business_name: merged.business_name,
        usage: merged.usage,
        churned: merged.churned,
        enable_slack: merged.enable_slack,
        enable_teams: merged.enable_teams,
        enable_twilio: merged.enable_twilio,
        enable_whatsapp: merged.enable_whatsapp,
        enable_work_email: merged.enable_work_email,
        enable_personal_email: merged.enable_personal_email,
        ses_configuration_set: merged.ses_configuration_set,
        ...merged.limits,
      });

      onSaved(merged);
    },
    [account, accountUpdated, onSaved],
    "ce_admin",
  );
};
